import { Directive, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ActionsSubject, Store } from '@ngrx/store';
import { ofType } from '@ngrx/effects';

import { map, takeUntil } from 'rxjs/operators';
import * as NgrxWidgetActions from '../store/actions/widget.actions';
import { WidgetUtils } from './widget.utils';
import {
  DocumentInfo,
  LOG_INTERNAL_ID,
  ND,
  PtPermissions,
  StoreTemplateStatus,
  StoreWidget,
  StoreWidgetState,
  TemplateGroup,
  UiWidget,
  WidgetConfiguration,
  WidgetNotification,
  WidgetNotificationAction,
  WidgetNotificationLevel,
  WidgetValidator,
  WProperties
} from '@pt/pt-models';
import { WidgetInputDirective } from '../directives/widget-input.directive';
import { WidgetStatus } from '../widget-status';
import * as NgrxWidgetSelectors from '../store/widget.selectors';
import { TranslateService } from '@ngx-translate/core';
import { WidgetDialogService } from '../services/widget-dialog.service';
import { WidgetInternalStatus } from '../widget-internal-status';
import { NgxPermissionsService } from 'ngx-permissions';
import { WidgetDpiaV0Preview } from './widget-dpia/v0/models/widget-dpia-v0-preview';
import { Chapter } from 'it/primatoo/openapi/api/org/schemas/common/docs/_listtemplatechapterresponse/Chapter';
import { TreatmentWarning } from 'it/primatoo/openapi/api/org/schemas/treatment/TreatmentWarning';
import { Widget } from 'it/primatoo/openapi/api/org/schemas/common/docs/Widget';
import { WidgetType } from 'it/primatoo/openapi/api/org/schemas/common/docs/WidgetType';
import { ChapterSelector } from 'it/primatoo/openapi/api/org/schemas/common/docs/ChapterSelector';

/**
 * <E> indica il tipo di valore per la preview del widget
 */

@Directive()
export abstract class AbstractWidgetDirective<E>
  extends WidgetInputDirective
  implements OnInit, OnChanges, OnDestroy
{
  public properties?: WProperties = WProperties.defaultValue();
  public idPrefix;
  public previewData?: E;
  public comment: string;
  public unsavedComment: string;
  public widgetStatus: StoreWidgetState;
  public internalStatus: WidgetInternalStatus;
  protected destroy$: Subject<boolean> = new Subject<boolean>();
  protected isValid$: Subject<boolean> = new Subject();
  private autosave$: Observable<boolean> = this.store.select(
    NgrxWidgetSelectors.autosave
  );
  private getStoreWidget$: Observable<StoreWidget[]> = this.store.select(
    NgrxWidgetSelectors.getStoreWidget
  );
  private chapterGroups$: Observable<TemplateGroup[]> = this.store.select(
    NgrxWidgetSelectors.getChapterGroups
  );
  private docInfo$: Observable<DocumentInfo> = this.store.select(
    NgrxWidgetSelectors.getDocInfo
  );
  private chapter$: Observable<Chapter> = this.store.select(
    NgrxWidgetSelectors.getActiveChapterInfo
  );
  public warnings$: Observable<TreatmentWarning[]> = this.store.select(
    NgrxWidgetSelectors.warnings
  );
  protected testId: number[] = LOG_INTERNAL_ID;
  private _loop;
  private widgetGroup: TemplateGroup;
  public noDataLabel?: string;

  protected constructor(
    public store: Store,
    public actionsSubject: ActionsSubject,
    public translateService: TranslateService,
    public widgetDialogService: WidgetDialogService,
    public permissionService: NgxPermissionsService
  ) {
    super();
    this.idPrefix = WidgetUtils.generateId('w_');
  }

  private _widget: UiWidget;

  get widget(): UiWidget {
    return this._widget;
  }

  ngOnInit(): void {
    const id = this.initializationWidget.internalId;
    const supportEditMode = this.initializationWidget.editable;
    const isOptional = this.initializationWidget.opt
      ? this.initializationWidget.opt
      : false;
    const isEnabled = this.initializationWidget.enabled
      ? this.initializationWidget.enabled
      : false;
    this.properties = WProperties.create(this.initializationWidget.properties);
    this.internalStatus = new WidgetInternalStatus(
      id,
      supportEditMode,
      isOptional,
      isEnabled,
      !this.properties.skipComment,
      this.configuration()
    );
    this._widget = JSON.parse(JSON.stringify(this.initializationWidget));
    this.comment = this.widget.comment;
    this.unsavedComment = this.widget.comment;

    this.loadNoData(this._widget);
    this.beforeOnInit();

    this.autosave$
      .pipe(takeUntil(this.destroy$))
      .subscribe((autosave: boolean) => {
        this.internalStatus.setIsAutoSave(autosave);
      });

    this.isValid$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isValid: boolean) => {
        if (this.internalStatus.isValid !== isValid) {
          this.internalStatus.setIsValid(isValid);
          this.store.dispatch(
            NgrxWidgetActions.saveWidgetValidation({
              internalId: this.widget.internalId,
              valid: isValid,
            })
          );
        }
      });

    this.warnings$
      .pipe(takeUntil(this.destroy$))
      .subscribe((warnings?: TreatmentWarning[]) => {
        this.internalStatus.setWarning(warnings || []);
      });

    this.docInfo$
      .pipe(takeUntil(this.destroy$))
      .subscribe((info?: DocumentInfo) => {
        if (info) {
          this.internalStatus.setDocInfo(info.docId, info.docType);
          this.internalStatus.setIsDraft(info.status === 'DRAFT');
        }
      });

    this.chapter$
      .pipe(takeUntil(this.destroy$))
      .subscribe((chapter: Chapter) => {
        this.internalStatus.setChapter(chapter);
      });

    this.internalStatus.setIsMyLocked(this.lockAcquired);
    const permission: PtPermissions[] = Object.keys(
      this.permissionService.getPermissions()
    ) as PtPermissions[];
    this.internalStatus.setPermission(permission);

    this.actionsSubject
      .pipe(
        ofType(NgrxWidgetActions.reloadWidgetAfterSave),
        takeUntil(this.destroy$),
        map((a) => a.widgets)
      )
      .subscribe((widgets: UiWidget[]) => {
        if (widgets) {
          const current: UiWidget = widgets?.find(
            (w) => w.internalId === this.initializationWidget?.internalId
          );
          if (current) {
            console.log('CURRNET ', current);
            this._widget = JSON.parse(JSON.stringify(current));

            this.comment = this._widget.comment;
            this.unsavedComment = this._widget.comment;

            this.internalStatus.setOptional(this._widget.opt);
            this.internalStatus.setEnabled(this._widget.enabled);
            this.loadNoData(this._widget);
            if (!this.internalStatus.isAutoSave) {
              this.loadWidget(this.widget);
            } else {
              if (this.forceReloadWidget()) {
                // la DPIA necessita il ricaricamento delle query dopo il salvataggio
                this.loadWidget(this.widget);
              } else {
                this.dispatchCompleteState(this.previewData, false);
              }
            }
          }
        }
      });

    this.actionsSubject
      .pipe(
        ofType(NgrxWidgetActions.reloadWidget),
        takeUntil(this.destroy$),
        map((a) => a.widgets)
      )
      .subscribe((widgets: UiWidget[]) => {
        if (widgets) {
          const current = widgets?.find(
            (w) => w.internalId === this.initializationWidget?.internalId
          );
          if (current) {
            this._widget = JSON.parse(JSON.stringify(current));
            this.comment = this.widget.comment;
            this.unsavedComment = this.widget.comment;
            this.loadNoData(this._widget);
            this.loadWidget(this.widget);
          }
        }
      });

    this.actionsSubject
      .pipe(
        ofType(NgrxWidgetActions.saveWidgetFromExternalRequest),
        takeUntil(this.destroy$),
        map((a) => a.internalId)
      )
      .subscribe((internalId: number) => {
        if (internalId === this.widget.internalId) {
          if (this.testId.includes(this.widget.internalId)) {
            console.log('[TEST] saveWidgetFromExternalRequest');
          }
          this.saveWidget(true);
        }
      });

    this.chapterGroups$
      .pipe(takeUntil(this.destroy$))
      .subscribe((groups: TemplateGroup[]) => {
        if (groups) {
          groups.find((widgetGroup: TemplateGroup) => {
            if (
              widgetGroup?.widgets?.find(
                (w) => w.internalId === this.widget.internalId
              )
            ) {
              this.widgetGroup = widgetGroup;
            }
          });
        }
      });

    this.getStoreWidget$
      .pipe(takeUntil(this.destroy$))
      .subscribe((widgets: StoreWidget[]) => {
        if (widgets) {
          const current = widgets?.find(
            (w: StoreWidget) =>
              w.internalId === this.initializationWidget?.internalId
          );
          if (
            current &&
            this.isChangeStoreWidget(
              current,
              this.previewData,
              this.widgetStatus
            )
          ) {
            this.previewData = current.previewData;
            this.widgetStatus = current.state;
          }
        }
      });
    this.loadWidget(this.widget);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.lockAcquired &&
      changes.lockAcquired.currentValue !== null &&
      changes.lockAcquired.currentValue !== undefined &&
      changes.lockAcquired.currentValue !== this.internalStatus?.isMyLocked
    ) {
      if (this.internalStatus) {
        this.internalStatus.setIsMyLocked(changes.lockAcquired.currentValue);
      }
    }

    if (changes.status && this.widget) {
      const s: StoreTemplateStatus = changes.status.currentValue;
      const isLoading = !s?.loadedWidgetId?.includes(this.widget?.internalId);
      const actualEditModeWidgetId = s.activedEditWidgetId;
      const isInitializedTemplate = s.editorReady;
      const hasValue = !this.isNoData();

      this.internalStatus.setStatus(
        isLoading,
        actualEditModeWidgetId,
        isInitializedTemplate,
        hasValue
      );
    }

    if (changes.widget) {
      this._widget = JSON.parse(JSON.stringify(this.widget));
      this.comment = this.widget.comment;
      this.unsavedComment = this.widget.comment;
      this.loadNoData(this._widget);
      this.loadWidget(this._widget);
    }
  }

  ngOnDestroy(): void {
    if (this._loop) {
      clearInterval(this._loop);
    }
    this.destroy$.next(true);
    this.afterOnDestroy();
  }

  createOptWidget(): void {
    this.internalStatus.setUserRemoveOpt(true);
    this.dispatchEditState();
  }

  closeEditor(widget: UiWidget) {
    this.resetWidget(widget);
  }

  resetWidget(widget: UiWidget) {
    this.internalStatus.setUserRemoveOpt(false);
    this.loadWidget(widget);
  }

  reloadWidget(widget: UiWidget) {
    this.internalStatus.setUserRemoveOpt(false);
    this.loadWidget(widget);
  }

  clickCommentWidget() {
    const title: Observable<string> = this.translateService.stream('TOOLTIP.COMMENT');
    const value: string = this.comment;
    const editable = !this.internalStatus.disabledCommentButton;
    const description = editable ? this.translateService.stream('MESSAGE.WIDGET_COMMENT_ENABLED') : this.translateService.stream('MESSAGE.WIDGET_COMMENT_DISABLED');

    const dialog = this.widgetDialogService.runComment(title, value, editable, description);
    dialog.subscribe((value) => {
      if (this.comment !== value) {
        if (this.testId.includes(this.widget.internalId)) {
          console.log('[TEST] Change Comment');
        }
        this.comment = value;
        this.changeComment(this.comment);
      } else {
        if (this.testId.includes(this.widget.internalId)) {
          console.log('[TEST] Not Changed Comment');
        }
      }
    }, () => {
      if (this.testId.includes(this.widget.internalId)) {
        console.log('[TEST] Dismiss Comment Dialog');
      }
    });
  }

  sendWidgetNotificationInvalidQueryResponseInvalidValue(payload: UiWidget) {
    if (this.internalStatus.isDraft) {
      this.sendWidgetNotification(
        this.translateService.stream(
          'WIDGET_NOTIFICATION.INVALID_QUERY_RESPONSE.INVALID_VALUE'
        ),
        // 'show-and-save',
        'show',
        'warning',
        payload
      );
    } else {
      this.sendWidgetNotification(
        this.translateService.stream(
          'WIDGET_NOTIFICATION.INVALID_QUERY_RESPONSE.INVALID_VALUE'
        ),
        'show-and-reopen',
        'warning',
        {
          docId: this.internalStatus.docId,
          docType: this.internalStatus.docType,
        }
      );
    }
  }

  sendWidgetNotificationInvalidQueryResponseNoData(
    payload?: UiWidget | unknown
  ) {
    this.sendWidgetNotificationInvalidQueryResponse(
      this.translateService.stream(
        'WIDGET_NOTIFICATION.INVALID_QUERY_RESPONSE.NO_DATA'
      ),
      payload
    );
  }

  sendWidgetInvalidValidation(payload?: WidgetValidator) {
    this.sendWidgetNotificationInvalidQueryResponse(
      this.translateService.stream('WIDGET_NOTIFICATION.INVALID_VALIDATION'),
      payload
    );
  }

  sendWidgetNotificationInvalidQueryResponse(
    message: Observable<string>,
    payload?: UiWidget | unknown
  ) {
    this.sendWidgetNotification(message, 'show', 'warning', payload);
  }

  sendWidgetNotification(
    message: Observable<string>,
    action: WidgetNotificationAction,
    level: WidgetNotificationLevel,
    payload?: UiWidget | unknown
  ) {
    let groupLabel = ND;
    if (this.widgetGroup) {
      groupLabel = this.widgetGroup.name;
    }
    const notification: WidgetNotification = {
      widget: this._widget,
      message,
      action,
      level,
      groupLabel,
      payload,
    };
    this.store.dispatch(
      NgrxWidgetActions.emitWidgetNotification({ notification })
    );
  }

  dispatchEditState() {
    console.log('dispatchEditState');
    this.store.dispatch(
      NgrxWidgetActions.setWidgetState({
        widgetStatus: {
          internalId: this.widget?.internalId,
          version: this.widget?.version,
          type: this.widget?.type,
          state: StoreWidgetState.COMPLETED,
          valid: this.internalStatus.isValid,
          activeEditMode: true,
          previewData: this.previewData,
          opt: this.internalStatus.showOptButton,
        } as WidgetStatus,
      })
    );
  }

  beforeOnInit?(): void {
    /* empty */
  }

  afterOnDestroy?(): void {
    /* empty */
  }

  // active Chapters qui non è usato per adesso
  public completeSaveWidget(
    widget: UiWidget,
    ignoreTrigger: boolean,
    resetWarning?: TreatmentWarning[],
    activeChapter?: ChapterSelector[]
  ) {
    const w = JSON.parse(JSON.stringify(widget));
    w.comment = this.comment;

    if (ignoreTrigger) {
      const request: Widget = WidgetUtils.toWidgetRequest(
        w,
        this.internalStatus.activeChapter.selector
      );
      this.store.dispatch(
        NgrxWidgetActions.saveWidgets({
          widgets: [request],
          resetWarning: resetWarning,
          activeChapter: activeChapter,
        })
      );
      this.store.dispatch(
        NgrxWidgetActions.saveWidgetValidation({
          internalId: this.widget.internalId,
          valid: this.internalStatus.isValid,
        })
      );
    } else {
      if (this.testId.includes(this.widget.internalId)) {
        console.log('[TEST] completeSaveWidget ', ignoreTrigger);
      }
      this.store.dispatch(
        NgrxWidgetActions.execTriggerWidget({
          widget: w,
          resetWarning: resetWarning,
        })
      );
      this.store.dispatch(
        NgrxWidgetActions.saveWidgetValidation({
          internalId: this.widget.internalId,
          valid: this.internalStatus.isValid,
        })
      );
    }
  }

  public isNoData(): boolean {
    return (
      this._widget?.value === null ||
      this._widget?.value === undefined ||
      this._widget?.value?.length === 0
    );
  }

  abstract loadWidget(widget: UiWidget): void;

  abstract saveWidget(
    ignoreTrigger?: boolean,
    resetWarning?: TreatmentWarning[]
  ): void;

  abstract configuration(): WidgetConfiguration;

  protected dispatchCompleteState(previewData: E, activeEditMode = false) {
    // TODO rimouvere valid dal set state
    //@giovanni prima di un salvataggio solitamente il widget effettua un checkValidity che modifica il valore di isValid$.
    setTimeout(() => {
      // console.warn('[DEV] setWidgetState 02');
      this.store.dispatch(
        NgrxWidgetActions.setWidgetState({
          widgetStatus: {
            internalId: this.widget?.internalId,
            version: this.widget?.version,
            type: this.widget?.type,
            state: StoreWidgetState.COMPLETED,
            valid: this.internalStatus.isValid,
            activeEditMode,
            previewData,
            opt: this.internalStatus.showOptButton,
          } as WidgetStatus,
        })
      );
    });
  }

  protected dispatchInitState() {
    if (!this._widget.saved) {
      this.sendWidgetNotification(
        this.translateService.stream('WIDGET_NOTIFICATION.WIDGET_NOT_SAVED'),
        'show',
        'warning'
      );
    }

    this.store.dispatch(
      NgrxWidgetActions.setWidgetState({
        widgetStatus: {
          internalId: this.widget?.internalId,
          version: this.widget?.version,
          type: this.widget?.type,
          state: StoreWidgetState.INITIALIZATION,
          valid: this.internalStatus.isValid,
          activeEditMode: false,
          previewData: this.previewData,
          opt: this.internalStatus.showOptButton,
        } as WidgetStatus,
      })
    );
  }

  protected dispatchErrorState() {
    this.store.dispatch(
      NgrxWidgetActions.setWidgetState({
        widgetStatus: {
          internalId: this.widget?.internalId,
          version: this.widget?.version,
          type: this.widget?.type,
          state: StoreWidgetState.ERROR,
          valid: this.internalStatus.isValid,
          activeEditMode: false,
          previewData: this.previewData,
          opt: this.internalStatus.showOptButton,
        } as WidgetStatus,
      })
    );
  }

  private forceReloadWidget() {
    switch (true) {
      case this._widget.type === WidgetType.DPIA: {
        if (this.previewData) {
          const p: WidgetDpiaV0Preview = this
            .previewData as WidgetDpiaV0Preview;
          const damageType = p.value?.damageType;
          const widgetDamageType = this._widget?.value?.damageType;
          if (damageType !== widgetDamageType) {
            // Ho cambiato il tipo di DPIA
            return true;
          }
        }
        return false;
      }
      case this._widget.type === WidgetType.RISK_ASSESSMENT:
      case this._widget.type === WidgetType.APP_SUMMARY:
        return true;
      default:
        return false;
    }
  }

  private loadNoData(widget: UiWidget) {
    if (
      widget?.args?.header?.noDataLabel &&
      widget.args.header.noDataLabel.length > 0
    ) {
      this.noDataLabel = widget.args.header.noDataLabel;
    }
  }

  private isChangeStoreWidget(
    current: StoreWidget,
    previewData: E,
    widgetStatus: StoreWidgetState
  ) {
    return (
      JSON.stringify(current.previewData) !== JSON.stringify(previewData) ||
      current.state !== widgetStatus
    );
  }

  private changeComment(comment?: string): void {
    console.log('this.widget.internalId ', this.widget.internalId);
    if (this.internalStatus.isAutoSave || !this.internalStatus.disabledCommentButton) {
      if (this.testId.includes(this.widget.internalId)) {
        console.log('[TEST] changeComment ', comment);
      }
      this.saveWidget();
    }
  }
}
